import {
    Alert,
    AlertDescription,
    AlertIcon,
    AlertTitle,
    Badge,
    Box,
    Button,
    Card,
    CardBody,
    Code,
    Flex,
    HStack,
    Icon,
    IconButton,
    Skeleton,
    SkeletonText,
    Spinner,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tooltip,
    Tr,
    useBreakpointValue,
    useColorModeValue,
    useToast,
    VStack,
} from '@chakra-ui/react';
import React from 'react';
import {
    FiAlertCircle,
    FiCalendar,
    FiCheckCircle,
    FiClock,
    FiCopy,
    FiEye,
    FiMapPin,
    FiMoreHorizontal,
    FiRefreshCw,
    FiSmartphone,
    FiTag,
    FiX,
} from 'react-icons/fi';
import { RegistrationRecord } from '../hooks/useRegistrationList';

interface RegistrationListProps {
  inscricoes: RegistrationRecord[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  onLoadMore: () => void;
  onRefresh: () => void;
  onCopyAccessCode: (code: string) => Promise<boolean>;
}

const RegistrationList: React.FC<RegistrationListProps> = ({
  inscricoes,
  loading,
  loadingMore,
  hasMore,
  error,
  onLoadMore,
  onRefresh,
  onCopyAccessCode,
}) => {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const bgColor = useColorModeValue('white', 'gray.800');
  const tableHeaderBg = useColorModeValue('gray.50', 'gray.700');
  const tableHoverBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const toast = useToast();

  const handleCopyCode = async (code: string) => {
    const success = await onCopyAccessCode(code);
    
    toast({
      title: success ? 'Código copiado!' : 'Erro ao copiar',
      description: success 
        ? `Código ${code} copiado para a área de transferência`
        : 'Não foi possível copiar o código. Tente novamente.',
      status: success ? 'success' : 'error',
      duration: 3000,
      isClosable: true,
    });
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString('pt-BR');
    } catch {
      return dateString;
    }
  };

  // Loading inicial
  if (loading && inscricoes.length === 0) {
    return (
      <Card>
        <CardBody>
          <VStack spacing={4}>
            {Array.from({ length: 5 }).map((_, index) => (
              <Box key={index} w="100%" p={4} bg={bgColor} borderRadius="md">
                <HStack spacing={4}>
                  <Skeleton height="20px" width="200px" />
                  <Skeleton height="20px" width="150px" />
                  <Skeleton height="20px" width="100px" />
                </HStack>
                <SkeletonText mt={2} noOfLines={3} spacing={2} />
              </Box>
            ))}
          </VStack>
        </CardBody>
      </Card>
    );
  }

  // Estado de erro
  if (error && inscricoes.length === 0) {
    return (
      <Card>
        <CardBody>
          <Alert status="error" borderRadius="lg">
            <AlertIcon />
            <Box>
              <AlertTitle>Erro ao carregar inscrições!</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Box>
          </Alert>
          <Button
            leftIcon={<FiRefreshCw />}
            colorScheme="blue"
            onClick={onRefresh}
            mt={4}
          >
            Tentar Novamente
          </Button>
        </CardBody>
      </Card>
    );
  }

  // Estado vazio
  if (!loading && inscricoes.length === 0) {
    return (
      <Card>
        <CardBody>
          <VStack spacing={4} py={8}>
            <Icon as={FiAlertCircle} boxSize={16} color="gray.400" />
            <Text fontSize="lg" color="gray.600" textAlign="center">
              Nenhuma inscrição encontrada
            </Text>
            <Text fontSize="sm" color="gray.500" textAlign="center">
              Tente ajustar os filtros ou verifique novamente mais tarde
            </Text>
            <Button leftIcon={<FiRefreshCw />} onClick={onRefresh} variant="outline">
              Atualizar
            </Button>
          </VStack>
        </CardBody>
      </Card>
    );
  }

  // Renderização mobile (cards)
  if (isMobile) {
    return (
      <VStack spacing={4}>
        {inscricoes.map((inscricao) => (
          <Card key={inscricao.id} w="100%">
            <CardBody>
              <VStack align="start" spacing={3}>
                <HStack justify="space-between" w="100%">
                  <VStack align="start" spacing={1}>
                    <Text fontWeight="bold" color="blue.600">
                      {inscricao.usuarioNome}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {inscricao.usuarioDocumento}
                    </Text>
                  </VStack>
                  <Badge
                    colorScheme={inscricao.temCheckin ? 'green' : 'red'}
                    variant="solid"
                  >
                    {inscricao.temCheckin ? '✅ Check-in OK' : '❌ Pendente'}
                  </Badge>
                </HStack>

                {/* Código de Acesso - DESTAQUE ESPECIAL */}
                <Box 
                  w="100%" 
                  p={3} 
                  bg="blue.50" 
                  borderRadius="lg" 
                  border="2px solid" 
                  borderColor="blue.200"
                >
                  <HStack justify="space-between" align="center">
                    <VStack align="start" spacing={1}>
                      <HStack>
                        <Icon as={FiSmartphone} color="blue.600" />
                        <Text fontSize="sm" fontWeight="medium" color="blue.800">
                          Código QR para Acesso
                        </Text>
                      </HStack>
                      <Code 
                        fontSize="lg" 
                        fontWeight="bold" 
                        color="blue.900" 
                        bg="blue.100"
                        p={2}
                      >
                        {inscricao.codigoAcesso}
                      </Code>
                    </VStack>
                    <Tooltip label="Copiar código">
                      <IconButton
                        aria-label="Copiar código"
                        icon={<FiCopy />}
                        colorScheme="blue"
                        variant="outline"
                        size="sm"
                        onClick={() => handleCopyCode(inscricao.codigoAcesso)}
                      />
                    </Tooltip>
                  </HStack>
                </Box>

                <VStack align="start" spacing={2} w="100%">
                  <HStack>
                    <Icon as={FiMapPin} color="gray.500" />
                    <Text fontSize="sm">
                      {inscricao.loja}{inscricao.sala ? ` - ${inscricao.sala}` : ''}
                    </Text>
                  </HStack>
                  
                  <HStack>
                    <Icon as={FiCalendar} color="gray.500" />
                    <Text fontSize="sm">
                      Inscrição: {inscricao.dataInscricaoFormatada}
                    </Text>
                  </HStack>

                  <HStack>
                    <Icon as={FiTag} color="gray.500" />
                    <Text fontSize="sm">
                      Etiqueta: {inscricao.etiqueta?.nome || 'N/A'}
                    </Text>
                  </HStack>

                  <HStack>
                    <Icon as={FiEye} color="gray.500" />
                    <Text fontSize="sm">
                      {inscricao.totalAcessos} acesso{inscricao.totalAcessos !== 1 ? 's' : ''}
                    </Text>
                  </HStack>

                  {inscricao.ultimoAcesso && (
                    <HStack>
                      <Icon as={FiClock} color="gray.500" />
                      <Text fontSize="sm">
                        Último: {formatDateTime(inscricao.ultimoAcesso)}
                      </Text>
                    </HStack>
                  )}
                </VStack>
              </VStack>
            </CardBody>
          </Card>
        ))}

        {/* Botão Carregar Mais - Mobile */}
        <Box w="100%" textAlign="center" py={4}>
          {error && inscricoes.length > 0 && (
            <Alert status="error" borderRadius="lg" mb={4}>
              <AlertIcon />
              <Text>{error}</Text>
            </Alert>
          )}
          
          <Button
            onClick={onLoadMore}
            disabled={!hasMore || loadingMore}
            size="lg"
            colorScheme="blue"
            variant={hasMore ? 'solid' : 'outline'}
            w="100%"
            leftIcon={loadingMore ? <Spinner size="sm" /> : <FiMoreHorizontal />}
          >
            {loadingMore
              ? 'Carregando...'
              : hasMore
              ? `Carregar Mais`
              : 'Todos os registros carregados'}
          </Button>
        </Box>
      </VStack>
    );
  }

  // Renderização desktop (tabela)
  return (
    <Card>
      <CardBody p={0}>
        <Box overflowX="auto">
          <Table variant="simple">
            <Thead bg={tableHeaderBg}>
              <Tr>
                <Th>Nome</Th>
                <Th>Documento</Th>
                <Th>Email</Th>
                <Th>📱 Código QR</Th>
                <Th>Loja/Sala</Th>
                <Th>Telefone</Th>
                <Th>Data Inscrição</Th>
                <Th>Etiqueta</Th>
                <Th>Check-in</Th>
                <Th>Acessos</Th>
                <Th>Último Acesso</Th>
              </Tr>
            </Thead>
            <Tbody>
              {inscricoes.map((inscricao) => (
                <Tr key={inscricao.id} _hover={{ bg: tableHoverBg }}>
                  <Td>
                    <Text fontWeight="medium" color="blue.600">
                      {inscricao.usuarioNome}
                    </Text>
                  </Td>
                  <Td>
                    <Text fontSize="sm">{inscricao.usuarioDocumento}</Text>
                  </Td>
                  <Td>
                    <Text fontSize="sm">{inscricao.usuarioEmail}</Text>
                  </Td>
                  <Td>
                    {/* Código de Acesso - DESTAQUE ESPECIAL */}
                    <Flex align="center" gap={2}>
                      <Box
                        bg="blue.50"
                        p={2}
                        borderRadius="md"
                        border="1px solid"
                        borderColor="blue.200"
                      >
                        <Code 
                          fontSize="sm" 
                          fontWeight="bold" 
                          color="blue.900"
                          bg="transparent"
                        >
                          {inscricao.codigoAcesso}
                        </Code>
                      </Box>
                      <Tooltip label="Copiar código QR">
                        <IconButton
                          aria-label="Copiar código QR"
                          icon={<FiCopy />}
                          size="xs"
                          colorScheme="blue"
                          variant="outline"
                          onClick={() => handleCopyCode(inscricao.codigoAcesso)}
                        />
                      </Tooltip>
                    </Flex>
                  </Td>
                  <Td>
                    <VStack align="start" spacing={0}>
                      <Text fontSize="sm" fontWeight="medium">
                        {inscricao.loja}
                      </Text>
                      {inscricao.sala && (
                        <Text fontSize="xs" color="gray.500">
                          {inscricao.sala}
                        </Text>
                      )}
                    </VStack>
                  </Td>
                  <Td>
                    <Text fontSize="sm">{inscricao.usuarioTelefone}</Text>
                  </Td>
                  <Td>
                    <Text fontSize="sm">{inscricao.dataInscricaoFormatada}</Text>
                  </Td>
                  <Td>
                    <Text fontSize="sm">{inscricao.etiqueta?.nome || 'N/A'}</Text>
                  </Td>
                  <Td>
                    <Badge
                      colorScheme={inscricao.temCheckin ? 'green' : 'red'}
                      variant="solid"
                      display="flex"
                      alignItems="center"
                      gap={1}
                    >
                      <Icon as={inscricao.temCheckin ? FiCheckCircle : FiX} />
                      {inscricao.temCheckin ? 'Realizado' : 'Pendente'}
                    </Badge>
                  </Td>
                  <Td>
                    <Text fontSize="sm" textAlign="center">
                      {inscricao.totalAcessos}
                    </Text>
                  </Td>
                  <Td>
                    <Text fontSize="sm">
                      {inscricao.ultimoAcesso ? formatDateTime(inscricao.ultimoAcesso) : '-'}
                    </Text>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>

        {/* Botão Carregar Mais - Desktop */}
        <Box textAlign="center" p={6} borderTop="1px solid" borderColor={borderColor}>
          {error && inscricoes.length > 0 && (
            <Alert status="error" borderRadius="lg" mb={4}>
              <AlertIcon />
              <Text>{error}</Text>
            </Alert>
          )}
          
          <Button
            onClick={onLoadMore}
            disabled={!hasMore || loadingMore}
            size="lg"
            colorScheme="blue"
            variant={hasMore ? 'solid' : 'outline'}
            leftIcon={loadingMore ? <Spinner size="sm" /> : <FiMoreHorizontal />}
          >
            {loadingMore
              ? 'Carregando...'
              : hasMore
              ? `Carregar Mais`
              : 'Todos os registros carregados'}
          </Button>
        </Box>
      </CardBody>
    </Card>
  );
};

export default RegistrationList; 